import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import type { CodeListItem } from '@models/codelist';
import type { ExclusivePriceGrid } from '@models/exclusive-prices';
import type { WaterPriceList } from '@models/water-price-list';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { finalize, type Observable, tap } from 'rxjs';
import { PriceListSelectComponent } from '../../shared/price-list-select/price-list-select.component';
import { parsePriceInput } from '../../shared/pricing-grid/parse-price-input';
import { resolvePriceListId, withPriceListInOrder } from '../../shared/pricing-grid/pricing-grid.model';
import { AddPriceListButtonComponent } from '../price-lists/add-price-list-button/add-price-list-button.component';
import { ExclusivePricingDataService } from './exclusive-pricing-data.service';
import { ExclusivePricingGridStore } from './exclusive-pricing-grid.store';

/**
 * "Exclusive booking" pricing section for a water (CS-286). Lets staff enter a purchase + selling price
 * for hiring the whole lake, per season × stay type, within one price list. Unlike the per-unit
 * combination grids (CS-285) the exclusive rate does not depend on the number of anglers, so the grid is
 * a single flat matrix: seasons as rows, stay types as columns, every cell visible at once.
 *
 * Both axes are per-water and resolved by the API from the **saved** water: rows are the seasons it has
 * periods for, columns the stay types its week/midweek/weekend flags enable. They come from the same
 * helper the pricing endpoints validate a save against, so a save can never be rejected for offering a
 * dimension the water does not have.
 *
 * Fetch granularity is one blob per price list: selecting a price list loads its full grid, and saving
 * PUTs the whole grid back (the API reconciles to minimal writes, deleting cells absent from the
 * payload).
 *
 * This is a section of the water form, not a screen of its own: the page's Save persists the water and
 * then folds in this grid, the same way the swim and accommodation forms fold in their combination grid.
 * The parent drives that through {@link validate}, {@link save} and {@link reload}, and owns the spinner
 * and every save toast.
 *
 * The editable cell map and all cell math live in {@link ExclusivePricingGridStore}; HTTP lives in
 * {@link ExclusivePricingDataService}.
 */
@Component({
  selector: 'app-exclusive-pricing',
  templateUrl: './exclusive-pricing.component.html',
  styleUrl: './exclusive-pricing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ExclusivePricingGridStore],
  imports: [
    DecimalPipe,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    AddPriceListButtonComponent,
    PriceListSelectComponent,
    TranslatePipe,
  ],
})
export class ExclusivePricingComponent implements OnInit {
  readonly waterId = input.required<number>();
  /** Whether the *saved* water permits exclusive hire; the grid is hidden otherwise. */
  readonly canEdit = input.required<boolean>();
  /** The exclusive setting has been changed in the form but not saved — the grid can't be trusted yet. */
  readonly hasUnsavedExclusiveChange = input.required<boolean>();
  /** The page's save is in flight; the price-list dropdown stays put until it lands. */
  readonly isSaving = input(false);

  private readonly gridStore = inject(ExclusivePricingGridStore);
  private readonly dataService = inject(ExclusivePricingDataService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  // Dimension 1 — price lists (years) for this water; switching is guarded by PriceListSelectComponent.
  readonly priceLists = signal<WaterPriceList[]>([]);
  readonly selectedPriceListId = signal<number | null>(null);

  // Dimension 2 — seasons this water is priced by (rows), labelled and ordered by the API.
  readonly seasonRows = signal<CodeListItem[]>([]);

  // Dimension 3 — stay types this water offers (columns).
  readonly stayTypeColumns = signal<CodeListItem[]>([]);

  /** The loaded grid header (price-list info); the editable cell map lives in the grid store. */
  protected readonly grid = signal<ExclusivePriceGrid | null>(null);

  readonly isLoading = signal(false);

  /**
   * The price lists and axes could not be read, and there is nothing left on screen to fall back on. Held
   * apart from an empty result so the section does not answer a failed load by asserting this water has no
   * price lists.
   */
  readonly loadFailed = signal(false);

  /** One price list's grid could not be read. Its cells are cleared deliberately — see {@link loadGrid}. */
  protected readonly gridLoadFailed = signal(false);

  // Edit + validation state is owned by the grid store; re-exposed for the template.
  readonly dirty = this.gridStore.dirty;
  readonly showErrors = this.gridStore.showErrors;
  readonly hasPartialCells = computed(() => this.gridStore.partialKeys().length > 0);
  readonly hasOutOfRangeCells = computed(() => this.gridStore.outOfRangeKeys().length > 0);

  readonly hasPriceLists = computed(() => this.priceLists().length > 0);
  readonly hasSeasons = computed(() => this.seasonRows().length > 0);
  readonly hasStayTypes = computed(() => this.stayTypeColumns().length > 0);
  readonly canRenderGrid = computed(
    () => this.grid() != null && this.canEdit() && this.hasSeasons() && this.hasStayTypes(),
  );

  /**
   * Discards responses from superseded loads. Switching price list quickly fires overlapping GETs, and
   * without this a slow earlier response can land last and overwrite the newer grid.
   */
  private loadToken = 0;

  ngOnInit(): void {
    // A water that can't be hired exclusively has no grid to show, so nothing is fetched for it. The
    // parent only renders this section once the saved water has loaded, and re-runs the load itself
    // through `reload()` when a save changes that answer.
    if (this.canEdit()) this.load();
  }

  /**
   * Re-read everything after the page has saved the water. Both axes and the exclusive gate are derived
   * from the *saved* water, so a changed rental mode, season period or stay-type flag only reaches the
   * grid through this.
   *
   * Deliberately unconditional: {@link canEdit} mirrors the saved water but only takes its new value on
   * the next change-detection pass, so branching on it here would read the pre-save answer. A load for a
   * water that just lost exclusive hire is harmless — the template hides the grid either way.
   */
  reload(): void {
    this.load();
  }

  /**
   * Adopt a change made in the Price lists section: a list added, renamed, re-dated or deleted. Only the
   * dropdown's options are re-read — deliberately not {@link reload}, which would re-fetch the grid and
   * discard cell edits the user has not saved, for a change that in most cases isn't even to the price
   * list they are editing. Falls through to a full load only when the loaded price list has itself gone,
   * since then there is no grid left to preserve.
   */
  priceListsChanged(): void {
    this.dataService
      .getPriceLists(this.waterId())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => null,
          this.translate.instant('waters.exclusive-prices.load-failed'),
        ),
      )
      .subscribe((priceLists) => {
        if (priceLists === null) return;

        const current = this.selectedPriceListId();
        if (current !== null && !priceLists.some((p) => p.id === current)) {
          this.load();
          return;
        }

        this.priceLists.set(priceLists);
        // A first price list for a lake that had none: nothing is loaded, so there is a grid to go and get.
        if (current === null && priceLists.length > 0) this.load();
      });
  }

  /** Adopt a price list created from this section's own add button, and price it straight away. */
  protected onPriceListCreated(created: WaterPriceList): void {
    this.priceLists.update((priceLists) => withPriceListInOrder(priceLists, created));
    // The new list is empty by definition, which is why it was added — switch to it. The add button has
    // already had any unsaved edits confirmed away, so this does not need to ask again.
    this.loadGrid(created.id);
  }

  /** Bound to the retry button the failed state offers; nothing else re-attempts a load on its own. */
  protected retryLoad(): void {
    this.load();
  }

  /** Retry the selected price list's grid after its load failed. */
  protected retryGrid(): void {
    const priceListId = this.selectedPriceListId();
    if (priceListId != null) this.loadGrid(priceListId);
  }

  private load(): void {
    this.isLoading.set(true);
    this.dataService
      .loadInitial(this.waterId())
      .pipe(
        // `null`, not empty axes: a failure is the absence of an answer, and adopting empties for it would
        // have the section report that this water has no price lists, seasons or stay types.
        showGenericErrorToast(
          this.toastService,
          () => null,
          this.translate.instant('waters.exclusive-prices.load-failed'),
        ),
      )
      .subscribe((loaded) => {
        if (loaded === null) {
          // Keep whatever is on screen; only a first load has nothing to fall back on. A failed post-save
          // reload leaves the axes stale at worst — the grid itself is already the PUT response.
          this.loadFailed.set(!this.hasPriceLists());
          this.isLoading.set(false);
          return;
        }
        this.loadFailed.set(false);

        const { priceLists, seasons, stayTypes } = loaded;
        this.priceLists.set(priceLists);
        this.seasonRows.set(seasons);
        this.stayTypeColumns.set(stayTypes);

        // Stay in the price list the user was working in across a reload. A first load — or one whose price
        // list has since gone — opens on the one in effect today, not the newest, which may be staged ahead.
        const next = resolvePriceListId(priceLists, this.selectedPriceListId());

        // `loadGrid` owns the spinner from here; only the nothing-to-load case has to clear it itself.
        if (next != null) {
          this.loadGrid(next);
        } else {
          this.selectedPriceListId.set(null);
          this.grid.set(null);
          this.gridStore.load([]);
          this.isLoading.set(false);
        }
      });
  }

  /** Load one price list's grid. Bound to the price-list dropdown, which confirms a dirty switch first. */
  protected loadGrid(priceListId: number): void {
    this.selectedPriceListId.set(priceListId);

    const token = ++this.loadToken;
    this.isLoading.set(true);
    this.dataService
      .getGrid(this.waterId(), priceListId)
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => null,
          this.translate.instant('waters.exclusive-prices.load-failed'),
        ),
        finalize(() => {
          if (token === this.loadToken) this.isLoading.set(false);
        }),
      )
      .subscribe((grid) => {
        if (token !== this.loadToken) return;
        // A failed grid load does clear the cells, unlike a failed `load()`: `selectedPriceListId` has
        // already moved to the price list being fetched, so keeping the previous list's cells would let a
        // save write them into it. `gridLoadFailed` says why the grid is gone instead of leaving the section
        // blank, and clearing it also takes `canRenderGrid` — and with it `save()` — out of play.
        this.gridLoadFailed.set(grid === null);
        this.grid.set(grid);
        this.gridStore.load(grid?.cells ?? []);
      });
  }

  // --- Cell editing (delegated to the grid store) ---

  getPurchase(seasonId: number, stayTypeId: number): number | null {
    return this.gridStore.getPurchase([seasonId, stayTypeId]);
  }

  getSelling(seasonId: number, stayTypeId: number): number | null {
    return this.gridStore.getSelling([seasonId, stayTypeId]);
  }

  margin(seasonId: number, stayTypeId: number): number | null {
    return this.gridStore.margin([seasonId, stayTypeId]);
  }

  isCellInvalid(seasonId: number, stayTypeId: number): boolean {
    return this.gridStore.isCellInvalid([seasonId, stayTypeId]);
  }

  /** Half-filled specifically — an out-of-range cell is outlined too, but needs a different message. */
  isCellPartial(seasonId: number, stayTypeId: number): boolean {
    return this.gridStore.isCellPartial([seasonId, stayTypeId]);
  }

  setPurchase(seasonId: number, stayTypeId: number, event: Event): void {
    this.gridStore.setPrice([seasonId, stayTypeId], 'purchasePrice', parsePriceInput(event));
  }

  setSelling(seasonId: number, stayTypeId: number, event: Event): void {
    this.gridStore.setPrice([seasonId, stayTypeId], 'sellingPrice', parsePriceInput(event));
  }

  // --- Submitted by the page's form (see WaterDetailsComponent.save) ---

  /**
   * Whether the grid is fit to send, revealing the inline cell errors when it isn't: every cell must have
   * both prices or neither, and no price may fall outside the range the API accepts. Checked before the
   * water itself is saved, so a half-filled cell costs nothing.
   */
  validate(): boolean {
    if (this.hasPartialCells() || this.hasOutOfRangeCells()) {
      this.gridStore.showErrors.set(true);
      return false;
    }
    this.gridStore.showErrors.set(false);
    return true;
  }

  /**
   * Build the PUT for the currently-loaded price-list grid, or `null` when there is nothing to send. The
   * parent chains this into its own submit and reports the outcome; the response is folded back in here so
   * anything the API changed — cells dropped for dimensions this water doesn't offer, prices rounded to
   * two decimals — is what the user ends up looking at.
   *
   * An empty grid is a legitimate save that clears the price list, matching how the combination grids
   * behave. Nothing is sent while a grid load is in flight, though: {@link loadGrid} moves
   * {@link selectedPriceListId} to the new price list straight away but only replaces the cells when the
   * GET lands, so in between a PUT would write the previous price list's prices into the new one.
   */
  save(): Observable<ExclusivePriceGrid> | null {
    const priceListId = this.selectedPriceListId();
    if (!this.canRenderGrid() || priceListId == null || this.isLoading()) return null;

    return this.dataService.saveGrid(this.waterId(), priceListId, { cells: this.gridStore.toPayload() }).pipe(
      tap((grid) => {
        this.grid.set(grid);
        this.gridStore.load(grid.cells);
      }),
    );
  }
}
